import React from "react";
import { Link } from "react-router-dom";
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin, 
  ArrowUp,
  Mail,
  Phone,
  MapPin
} from "lucide-react";
import { useLanguage } from "../../context/LanguageProvider";
import VisitorCounter from "../shared/VisitorCounter";

const Footer: React.FC = () => {
  const { t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-slate-300 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
          
          {/* Brand Column */}
          <div className="lg:col-span-4 space-y-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-primary rounded-xl flex items-center justify-center">
                  <span className="text-white font-black text-xl">F</span>
              </div>
              <span className="text-2xl font-black text-white tracking-tighter">FUKKONI</span>
            </div>
            <p className="text-slate-400 leading-relaxed pr-6">
              {t("footer_brand_description")}
            </p>
            
            <div className="flex space-x-4 pt-2">
              {[Facebook, Twitter, Instagram, Linkedin].map((Icon, i) => (
                <a key={i} href="#" className="h-10 w-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-primary transition-colors duration-300">
                  <Icon className="h-5 w-5 text-white" />
                </a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-white font-bold text-lg mb-4">{t("footer_title_product")}</h4>
            <ul className="space-y-3">
              <li><a href="#features" className="hover:text-white transition-colors">{t("nav_features")}</a></li>
              <li><a href="#solutions" className="hover:text-white transition-colors">{t("solutions_badge")}</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">{t("nav_pricing")}</a></li>
              <li><a href="#" className="hover:text-white transition-colors">{t("footer_link_updates")}</a></li>
            </ul>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-white font-bold text-lg mb-4">{t("footer_title_company")}</h4>
            <ul className="space-y-3">
              <li><a href="#" className="hover:text-white transition-colors">{t("nav_about")}</a></li>
              <li><a href="#" className="hover:text-white transition-colors">{t("footer_link_careers")}</a></li>
              <li><a href="#" className="hover:text-white transition-colors">{t("footer_link_blog")}</a></li>
              <li><a href="#" className="hover:text-white transition-colors">{t("footer_link_partners")}</a></li>
            </ul>
          </div>

          <div className="lg:col-span-4 space-y-6">
             <h4 className="text-white font-bold text-lg mb-4">{t("contact_info_title")}</h4>
             <ul className="space-y-4">
               <li className="flex items-start gap-4">
                  <Mail className="h-6 w-6 text-primary shrink-0" />
                  <span className="text-slate-400">{t("contact_info_email_address")}</span>
               </li>
               <li className="flex items-start gap-4">
                  <Phone className="h-6 w-6 text-primary shrink-0" />
                  <span className="text-slate-400">{t("contact_info_phone_number")}</span>
               </li>
               <li className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-primary shrink-0" />
                  <span className="text-slate-400">
                    {t("contact_info_office_address_line1")}, {t("contact_info_office_address_line2")}<br />
                    {t("contact_info_office_address_line3")}
                  </span>
               </li>
             </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-slate-500 text-sm">
            &copy; {currentYear} Fukkoni Inc. {t("footer_copyright_allRightsReserved")}
          </p>
          
          <div className="flex flex-col md:flex-row items-center gap-6">
             <div className="bg-slate-800/50 px-4 py-2 rounded-full border border-slate-700">
               <VisitorCounter />
             </div>
             
             <button 
                onClick={scrollToTop}
                className="h-10 w-10 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center hover:bg-primary hover:border-primary hover:text-white transition-all duration-300"
                aria-label={t("footer_back_to_top_aria")}
             >
                <ArrowUp className="h-5 w-5" />
             </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
